// ImageOverlay.jsx
// Component that shows an image overlay
// The image is clickable and can be expanded to show the full image.
// The image can also be opened in a new window.

// Copyright HS Analysis GmbH, 2024
// Author: Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// Material UI imports
import { makeStyles } from "@mui/styles";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

// HSA imports
import ValidatedImage from "./ValidatedImage";

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    width: 130,
    border: "1px solid #ccc",
    cursor: "pointer",
  },
  img: {
    width: "100%",
  },
  indicator: {
    position: "absolute",
    top: 0,
    right: 0,
  },
}));

const ImageOverlay = (props) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [isValid, setIsValid] = React.useState(false);

  const imageUrl = props.imageUrl;

  /**
   * Opens the dialog to show the enlarged image.
   */
  const handleImageClick = () => {
    setDialogOpen(true);
  };

  /**
   * Closes the dialog by setting the dialog open state to false.
   */
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Add this function to handle opening the image in a new window
  const handleOpenInNew = (e) => {
    e.stopPropagation(); // Prevent triggering handleImageClick
    const width = 800;
    const height = 600;
    const features = `width=${width},height=${height},toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,resizable=yes`;
    window.open(imageUrl, "_blank", features);
  };

  return (
    <>
      <div
        style={{
          display: isValid ? "block" : "none",
        }}
        className={classes.root}
        onClick={handleImageClick}
      >
        <ValidatedImage
          className={classes.img}
          draggable="false"
          src={imageUrl}
          alt="Slide Label"
          setIsValid={setIsValid}
        />
        <div className={classes.indicator}>
          {/* Remove expand/collapse icons */}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenInNew(e);
            }}
            aria-label="Open image in new window"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md">
        <DialogContent style={{ padding: 0 }}>
          <img
            src={imageUrl}
            alt="Slide Label"
            style={{ maxWidth: "90vw", maxHeight: "80vh", display: "block" }}
            draggable="false"
            onClick={handleDialogClose}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

ImageOverlay.propTypes = {
  imageUrl: PropTypes.string.isRequired,
};

export default ImageOverlay;
